import React, { FC, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { Button, Heading, Loading } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'

import { UserMetadata, UserNotification } from '../../../common/validations'
import {
  ContractType,
  ResidentNotificationCategory,
  ResidentNotificationPriority,
} from '../../../common/types'

import { useAPICloseUserNotification } from 'api/apiHooks/useAPICloseUserNotification'
import { params as useAPIGetUserNotificationsParams } from 'api/apiHooks/useAPIGetUserNotifications'
import { queryClient } from 'reactQuery'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'

const ResidentNotification: FC<{
  notification: UserNotification
  userMetadata: UserMetadata
  contractType?: ContractType
}> = ({ notification, userMetadata, contractType }) => {
  const { t } = useTranslation(['notifications'])
  const {
    mutate: handleClose,
    isPending,
    isSuccess,
  } = useAPICloseUserNotification(notification.id, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          // eslint-disable-next-line no-object-comparison/loose-type
          query.queryKey[0] === useAPIGetUserNotificationsParams.queryKey[0],
      })
    },
    onError: () => {
      // Silently handle expected errors when notification is already closed
    },
  })
  const navigate = useNavigate()
  const location = useLocation()
  const { messaging } = useModuleConfig()
  const contractTerminationApprovalTitleKey =
    contractType === ContractType.RIGHT_OF_OCCUPANCY
      ? 'notifications:contract_termination_request_approval_needed.rightOfOccupancyNoticeTitle'
      : 'notifications:contract_termination_request_approval_needed.title'

  const closeAttemptedRef = useRef(false)
  const {
    confirmContractTerminationPath,
    messagesPath,
    confirmContractModificationPath,
  } = useContractPaths()

  // removed separate invalidation effect – handled in mutation onSuccess

  useEffect(() => {
    const isOnMessagesPage = location.pathname.includes('/viestit')

    if (
      notification.category === ResidentNotificationCategory.message &&
      !notification.closedAt &&
      notification.isCloseable &&
      isOnMessagesPage &&
      !isPending &&
      !isSuccess &&
      !closeAttemptedRef.current
    ) {
      closeAttemptedRef.current = true
      handleClose()
    }
  }, [
    notification,
    location.pathname,
    messagesPath,
    handleClose,
    isPending,
    isSuccess,
  ])

  if (isPending) {
    return <Loading />
  }

  const renderCloseButton = () => {
    return (
      <div className="absolute top-1 right-0.5">
        <Button
          cancel
          disabled={
            isPending || !!notification.closedAt || userMetadata.isImpersonator
          }
          onClick={handleClose}
          icon="close-window"
        />
      </div>
    )
  }

  if (
    notification.category === ResidentNotificationCategory.message &&
    !messaging
  ) {
    return null
  }

  return (
    <div
      className={classNames(
        'min-h-16 min-w-80 p-3 rounded-md bg-common-blue-2 relative text-common-gray-7 cursor-pointer hover:outline hover:outline-common-blue-2 hover:outline-offset-2',
        {
          'text-common-gray-7 bg-common-blue-2':
            notification.priority === ResidentNotificationPriority.normal,
          'text-common-red-6 bg-common-red-2':
            notification.priority === ResidentNotificationPriority.important,
        }
      )}
    >
      {notification.category === ResidentNotificationCategory.arrear && (
        <div>
          <div className="text-common-red-6">{t('notifications:arrear')}</div>
          <div className="text-xl pt-2 text-common-red-6">
            -{notification.notificationMeta?.arrearAmount.replace('.', ',')} €
          </div>
        </div>
      )}
      {notification.category ===
        ResidentNotificationCategory.contract_termination_request_approval_needed && (
        <div>
          <div className={classNames('flex justify-between')}>
            <div>
              {t(
                'notifications:contract_termination_request_approval_needed.content'
              )}
            </div>
            <div className={classNames('flex justify-end')}>
              <Button
                icon="angle-right"
                iconPosition="right"
                onClick={() => navigate(confirmContractTerminationPath)}
                variant="pill"
                small
              >
                {t(
                  'notifications:contract_termination_request_approval_needed.readmore'
                )}
              </Button>
            </div>
          </div>
          <Heading
            element="h4"
            semibold
            size="m"
            className={classNames({
              'text-common-red-6':
                notification.priority ===
                ResidentNotificationPriority.important,
            })}
          >
            {t(contractTerminationApprovalTitleKey)}
          </Heading>
        </div>
      )}
      {notification.category ===
        ResidentNotificationCategory.contract_termination_request_tenant_rejected && (
        <div>
          <div className={classNames('pr-4')}>
            {t('notifications:contract_termination_request_tenant_rejected')}
          </div>
          {renderCloseButton()}
        </div>
      )}
      {notification.category ===
        ResidentNotificationCategory.contract_termination_request_kenno_rejected && (
        <div>
          <div className={classNames('pr-4')}>
            {t('notifications:contract_termination_request_kenno_rejected')}
          </div>
          {renderCloseButton()}
        </div>
      )}
      {notification.category === ResidentNotificationCategory.message && (
        <div>
          <div className={classNames('pr-4')}>{t('notifications:message')}</div>
          <div className="mt-2">
            <Button
              icon="angle-right"
              iconPosition="right"
              onClick={() => {
                if (!notification.closedAt && !userMetadata.isImpersonator) {
                  handleClose()
                }
                navigate(messagesPath)
              }}
              variant="pill"
              small
            >
              {t('notifications:viewMessages')}
            </Button>
          </div>
          {renderCloseButton()}
        </div>
      )}
      {notification.category ===
        ResidentNotificationCategory.contract_modification_request_approval_needed && (
        <div>
          <div className={classNames('flex justify-between')}>
            <div>
              {t(
                'notifications:contract_modification_request_approval_needed.content'
              )}
            </div>
            <div className={classNames('flex justify-end')}>
              <Button
                icon="angle-right"
                iconPosition="right"
                onClick={() => navigate(confirmContractModificationPath)}
                variant="pill"
                small
              >
                {t(
                  'notifications:contract_modification_request_approval_needed.readmore'
                )}
              </Button>
            </div>
          </div>
          <Heading
            element="h4"
            semibold
            size="m"
            className={classNames({
              'text-common-red-6':
                notification.priority ===
                ResidentNotificationPriority.important,
            })}
          >
            {t(
              'notifications:contract_modification_request_approval_needed.title'
            )}
          </Heading>
        </div>
      )}
    </div>
  )
}

export default ResidentNotification
